import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, tap, catchError, throwError, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SHOW_LOADER } from '../interceptors/micro-service-logging.interceptor';
import { ApiRequestOptions, ApiService } from './api.service';
import { botBuilderPath, callAuditorPath, gatewayUrl, isPrismNewUrl, prismNewUrl } from './bot-builder-routing';
import { ToasterService } from './toaster.service';
import { WhitelabelPartnerAuthService } from './whitelabel-partner-auth.service';

export type MicroServiceName = keyof typeof environment.microAppService_url;

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface MicroServiceRequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean> | HttpParams;
  withCredentials?: boolean;
  context?: HttpContext;
  showToast?: boolean;
  showLoader?: boolean;
  successMessage?: string;
  errorMessage?: string;
  includeProductId?: boolean;
  /** When true, do not auto-attach whitelabel partner auth credentials. */
  bypassWhitelabelPartnerAuth?: boolean;
}

@Injectable({ providedIn: 'root' })
export class MicroServiceHttpService {
  private readonly http = inject(HttpClient);
  private readonly serviceConfig = environment.microAppService_url;
  private readonly apiService = inject(ApiService);
  private readonly toasterService = inject(ToasterService);
  private readonly partnerAuth = inject(WhitelabelPartnerAuthService);

  private static readonly WHITELABEL_SERVICE_NAME = 'whitelabel' as const;
  private static readonly CALL_AUDITOR_SERVICE_NAME = 'callAuditor' as const;

  /**
   * URL for a whitelabel call made with fetch (SSE, keepalive, uploads): prism-new while it is
   * on (session auth, see {@link fetchWhitelabel}), else the gateway with its partner
   * credentials in the query.
   */
  whitelabelFetchUrl(path: string): string {
    const route = this.whitelabelRoute(path);
    return route !== null ? prismNewUrl(route) : this.partnerAuth.withQuery(gatewayUrl(path));
  }

  /**
   * fetch for a whitelabel URL. A prism-new call carries the prism session; on a 401 the session is
   * refreshed through ApiService's own refresh flow and the call retried once.
   */
  async fetchWhitelabel(url: string, init: RequestInit = {}): Promise<Response> {
    if (!isPrismNewUrl(url)) return fetch(url, init);
    const response = await fetch(url, this.withSession(init));
    if (response.status !== 401) return response;
    const stale = this.apiService.getAccessToken();
    await firstValueFrom(this.apiService.get('auth/whoami', undefined, { showErrorToaster: false }))
      .catch(() => undefined);
    const fresh = this.apiService.getAccessToken();
    return fresh && fresh !== stale ? fetch(url, this.withSession(init)) : response;
  }

  private withSession(init: RequestInit): RequestInit {
    const headers = new Headers(init.headers);
    const token = this.apiService.getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return { ...init, headers };
  }

  private resolveUrl(service: MicroServiceName, path: string): string {
    const cfg = this.serviceConfig[service];
    if (!cfg) {
      throw new Error(`[MicroServiceHttp] Service "${String(service)}" is not configured in environment.microAppService_url`);
    }
    const base = cfg.url.endsWith('/') ? cfg.url.slice(0, -1) : cfg.url;
    const segment = path.startsWith('/') ? path : `/${path}`;
    return `${base}${segment}`;
  }

  private buildHttpOptions(service: MicroServiceName, opts?: MicroServiceRequestOptions) {
    const cfg = this.serviceConfig[service];
    let headers = new HttpHeaders(opts?.headers ?? {});

    if (cfg.useAuth) {
      const token = sessionStorage.getItem('access_token');
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }

    let params =
      opts?.params instanceof HttpParams
        ? opts.params
        : new HttpParams({ fromObject: (opts?.params as Record<string, string>) ?? {} });

    if (
      service.includes(MicroServiceHttpService.WHITELABEL_SERVICE_NAME) &&
      opts?.bypassWhitelabelPartnerAuth !== true
    ) {
      const creds = this.partnerAuth.credentials();
      if (creds) {
        if (!params.has('authToken')) {
          params = params.set('authToken', creds.accessToken);
        }
        if (!params.has('secretKey')) {
          params = params.set('secretKey', creds.secretKey);
        }
        if (!params.has('productId')) {
          params = params.set('productId', String(creds.productPid));
        }
      }
    }

    if (opts?.includeProductId && !params.has('productId')) {
      const pid = this.selectedProductPid();
      if (pid !== null) {
        params = params.set('productId', String(pid));
      }
    }

    const context = (opts?.context ?? new HttpContext())
      .set(SHOW_LOADER, opts?.showLoader !== false);

    return {
      headers,
      params,
      withCredentials: opts?.withCredentials ?? cfg.withCredentials,
      context,
    };
  }

  /** Numeric pid of the product selected in the sidebar, or null when none resolves. */
  private selectedProductPid(): number | null {
    const selectedApiId =
      this.apiService.getSelectedProductId() ||
      sessionStorage.getItem('selected_product_id');
    if (!selectedApiId) return null;

    const products = this.apiService.products() as Array<{
      pid?: number | string;
      id?: string | number;
    }>;
    const pidRaw = products.find((p) => String(p.id) === String(selectedApiId))?.pid;
    const pid = pidRaw != null && String(pidRaw).trim() ? Number(pidRaw) : NaN;
    return Number.isFinite(pid) ? pid : null;
  }

  private withToast<T>(source$: Observable<T>, opts?: MicroServiceRequestOptions): Observable<T> {
    const showToast = opts?.showToast !== false;

    return source$.pipe(
      tap(() => {
        if (showToast) {
          this.toasterService.success(opts?.successMessage ?? 'Request successful');
        }
      }),
      catchError((error) => {
        if (showToast) {
          const status: number | undefined = error?.status;
          const message =
            status === 401
              ? 'You are not authorized.'
              : opts?.errorMessage ??
                error?.error?.message ??
                error?.message ??
                'Something went wrong';
          this.toasterService.error(message);
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * A whitelabel call prism-new serves natively: sent through ApiService (prism session, refresh on
   * 401, legacy body maps) with no partner credentials; only the product acted for travels along.
   * It is pointed at prism-new explicitly, since an unlisted route may be sent there too.
   */
  private sendToPrismNew<T>(
    method: HttpMethod,
    route: string,
    body: unknown,
    opts?: MicroServiceRequestOptions,
    withProductId = true,
  ): Observable<T> {
    const params = this.prismNewParams(opts, withProductId);
    const query = new URLSearchParams(params).toString();
    const url = query ? `${route}${route.includes('?') ? '&' : '?'}${query}` : route;
    const options: ApiRequestOptions = {
      baseUrl: environment.prismNew.apiUrl,
      rawBody: true,
      showErrorToaster: false,
      showLoader: opts?.showLoader !== false,
      headers: opts?.headers,
      withCredentials: opts?.withCredentials,
    };
    const call$ =
      method === 'GET' ? this.apiService.get<T>(route, params, options)
      : method === 'DELETE' ? this.apiService.delete<T>(url, options)
      : method === 'POST' ? this.apiService.post<T>(url, body ?? null, options)
      : method === 'PUT' ? this.apiService.put<T>(url, body ?? null, options)
      : this.apiService.patch<T>(url, body ?? null, options);
    // Callers read failures as HttpErrorResponse (`status`, `error` = body), as from HttpClient.
    return call$.pipe(
      catchError((error) => throwError(() =>
        error instanceof Error ? error : new HttpErrorResponse({ status: error?.statusCode ?? 0, error, url: route }),
      )),
    );
  }

  private prismNewParams(opts?: MicroServiceRequestOptions, withProductId = true): Record<string, string> {
    const params: Record<string, string> = {};
    const source = opts?.params;
    if (source instanceof HttpParams) {
      source.keys().forEach((key) => (params[key] = source.get(key) ?? ''));
    } else {
      Object.entries(source ?? {}).forEach(([key, value]) => (params[key] = String(value)));
    }
    const pid = withProductId ? this.selectedProductPid() : null;
    if (pid !== null && params['productId'] === undefined) {
      params['productId'] = String(pid);
    }
    return params;
  }

  /**
   * Where a whitelabel call goes: prism-new first. While prism-new routing is on, every
   * whitelabel call goes there, listed in prism-new-endpoints.ts or not, since bot-builder serves
   * the whole gateway surface and prism-new's get-setting hands out no gateway credentials. The
   * gateway, with the credentials the old API's settings carry, only when prism-new is off.
   */
  private whitelabelRoute(path: string): string | null {
    return environment.prismNew.enabled ? botBuilderPath(path) : null;
  }

  private send<T>(
    method: HttpMethod,
    service: MicroServiceName,
    path: string,
    body: unknown,
    opts?: MicroServiceRequestOptions,
  ): Observable<T> {
    const route = service === MicroServiceHttpService.WHITELABEL_SERVICE_NAME ? this.whitelabelRoute(path) : null;
    // Call auditor: prism-new serves the gateway surface natively on the session. The pages carry
    // their own productPid, so the sidebar product is not added as productId.
    const auditorRoute =
      service === MicroServiceHttpService.CALL_AUDITOR_SERVICE_NAME && environment.prismNew.enabled
        ? callAuditorPath(path)
        : null;
    const source$ = route
      ? this.sendToPrismNew<T>(method, route, body, opts)
      : auditorRoute
      ? this.sendToPrismNew<T>(method, auditorRoute, body, opts, false)
      : this.http.request<T>(method, this.resolveUrl(service, path), {
          ...this.buildHttpOptions(service, opts),
          body: method === 'GET' || method === 'DELETE' ? undefined : body ?? null,
        });
    return this.withToast(source$, opts);
  }

  get<T>(service: MicroServiceName, path: string, opts?: MicroServiceRequestOptions): Observable<T> {
    return this.send<T>('GET', service, path, undefined, opts);
  }

  post<T>(service: MicroServiceName, path: string, body?: unknown, opts?: MicroServiceRequestOptions): Observable<T> {
    return this.send<T>('POST', service, path, body, opts);
  }

  put<T>(service: MicroServiceName, path: string, body?: unknown, opts?: MicroServiceRequestOptions): Observable<T> {
    return this.send<T>('PUT', service, path, body, opts);
  }

  patch<T>(service: MicroServiceName, path: string, body?: unknown, opts?: MicroServiceRequestOptions): Observable<T> {
    return this.send<T>('PATCH', service, path, body, opts);
  }

  delete<T>(service: MicroServiceName, path: string, opts?: MicroServiceRequestOptions): Observable<T> {
    return this.send<T>('DELETE', service, path, undefined, opts);
  }
}
